import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useGuideBookings } from '@/features/guide/hooks/useGuideBookings';
import { BookingCard } from '@/features/guide/components/BookingCard';
import { BookingDetailDrawer } from '@/features/guide/components/BookingDetailDrawer';
import { GuidePageHeader } from '@/features/guide/components/GuidePageHeader';
import { BookingStatus } from '@/types';

type BookingTab =
  | 'upcoming'
  | 'in_progress'
  | 'awaiting'
  | 'pending'
  | 'disputed'
  | 'completed';

const TAB_STATUSES: Record<BookingTab, string | undefined> = {
  upcoming: `${BookingStatus.CONFIRMED},${BookingStatus.UPCOMING}`,
  in_progress: BookingStatus.IN_PROGRESS,
  awaiting: BookingStatus.WAITING_FOR_TRAVELER_CONFIRMATION,
  pending: BookingStatus.PAYMENT_PENDING,
  disputed: BookingStatus.DISPUTED,
  completed: BookingStatus.COMPLETED,
};

export function GuideBookingsPage() {
  const [tab, setTab] = useState<BookingTab>('upcoming');
  // `/dashboard/bookings/:id` (notification deep links) opens that booking's drawer.
  const { id: routeBookingId } = useParams<{ id: string }>();
  const [selectedId, setSelectedId] = useState<string | null>(routeBookingId ?? null);
  useEffect(() => {
    if (routeBookingId) setSelectedId(routeBookingId);
  }, [routeBookingId]);

  const statusParam = TAB_STATUSES[tab];
  const { data, isLoading, error } = useGuideBookings({
    statuses: statusParam?.includes(',') ? statusParam : undefined,
    status: statusParam?.includes(',') ? undefined : statusParam,
    page: 1,
    limit: 50,
  });

  const bookings = data?.data ?? [];

  return (
    <Box>
      <GuidePageHeader
        title="Bookings"
        subtitle="Manage paid reservations. Mark trips complete when finished."
      />

      <Tabs
        value={tab}
        onChange={(_, value: BookingTab) => setTab(value)}
        sx={{ mb: 2 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Upcoming" value="upcoming" />
        <Tab label="In progress" value="in_progress" />
        <Tab label="Awaiting confirmation" value="awaiting" />
        <Tab label="Pending payment" value="pending" />
        <Tab label="Disputed" value="disputed" />
        <Tab label="Completed" value="completed" />
      </Tabs>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load bookings. Please try again.
        </Alert>
      )}

      {!isLoading && !error && bookings.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 3,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            No bookings in this tab
          </Typography>
          <Typography variant="body2" color="text.secondary">
            When travelers book and pay, their trips will appear here.
          </Typography>
        </Paper>
      )}

      {bookings.map((booking) => (
        <BookingCard key={booking.id} booking={booking} onClick={() => setSelectedId(booking.id)} />
      ))}

      <BookingDetailDrawer bookingId={selectedId} onClose={() => setSelectedId(null)} />
    </Box>
  );
}
